import React, { memo } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { setGlobalModal, setGlobalModalMetadata } from 'actions/index'
import { sendCtaClickEvent } from 'helpers/segment'
import { useReviewFeatureLimits } from 'hooks/review_automation'

import { getDiffsFromForm } from 'helpers/review_automation'
import {
  Tooltip,
  Button,
  ButtonType,
  ToggleButton,
  ToggleButtonSizeType
} from '@junglescout/edna'

import {
  REVIEW_AUTOMATION_SURVEY,
  triggerUsabillaCampaign
} from 'services/usabilla_live'
import { QuestionTooltip } from 'ui_elements/QuestionTooltip/QuestionTooltip'
import { UpsellTooltip } from '../Upsell/UpsellTooltip'

const StyledLabel = styled.label`
  background-color: ${props => props.theme.colors[props.background]};
  border-radius: 2px;
  padding: 5px 8px;
  font-size: 12px;
`

/**
 * Returns a "Review Request Status" cell for Review Automation orders table, which contains toggle, status info,
 * tooltip (for some cases) and ability to Edit review request schedule
 * @param rowData <= "react-table" Cell component {content.row.original} data
 * @returns {JSX.Element}
 */
function OrderStatusCellComponent({ rowData: order, updateOrder }) {
  const { t } = useTranslation('generic')
  const dispatch = useDispatch()
  const { selectedAccount, selectedCountry } = useSelector(
    state => state.amazonSellerAccounts.amazonSellerAccountSelector
  )
  const { orderSkipEnabled, orderDelayEnabled } = useReviewFeatureLimits()

  const {
    statusColor,
    statusLabel,
    canEdit,
    toggleChecked,
    toggleDisabled,
    tooltip
  } = order.reviewAutomation

  const toggleAction = () => {
    const statusText = toggleChecked ? 'ON' : 'OFF'
    sendCtaClickEvent({
      text: `Review Request Status Column - Toggle ${statusText}`,
      location: 'Review Automation'
    })
    updateOrder({
      data: {
        ids: [order.order_id],
        marketplace_id: selectedCountry.marketplace_id,
        amazon_seller_account_id: selectedAccount.id,
        review_request_enabled: !toggleChecked
      },
      method: 'PUT'
    })

    // initiate survey if skipping orders
    if (!toggleChecked) {
      triggerUsabillaCampaign(REVIEW_AUTOMATION_SURVEY)
    }
  }

  const updateReviewSettings = params => {
    const { review_request_enabled } = params
    const text = review_request_enabled ? 'Delay Time Changed' : 'Skipped'
    sendCtaClickEvent({
      text: `Review Request Status Modal - Order Review Request ${text}`,
      location: 'Review Automation'
    })
    const payload = getDiffsFromForm(order, params)

    updateOrder({
      data: {
        ids: [order.order_id],
        marketplace_id: selectedCountry.marketplace_id,
        amazon_seller_account_id: selectedAccount.id,
        ...payload
      },
      method: 'PUT'
    })

    // initiate survey if skipping orders
    if (!review_request_enabled) {
      triggerUsabillaCampaign(REVIEW_AUTOMATION_SURVEY)
    }
  }

  const editOrderAction = () => {
    dispatch(setGlobalModal('OrderReviewModal'))
    dispatch(
      setGlobalModalMetadata({
        ...order,
        updateOrder: updateReviewSettings
      })
    )
  }

  const openUpsellModal = (type, enabled, initiator) => {
    if (!enabled) {
      dispatch(setGlobalModal('UpsellModal'))
      dispatch(setGlobalModalMetadata({ type, initiator }))
    }
  }

  const styledLabel = (
    <StyledLabel
      background={statusColor}
      onClick={() =>
        openUpsellModal('order_delay', orderDelayEnabled, 'Order Delayed')
      }>
      {statusLabel}
    </StyledLabel>
  )

  return (
    <>
      <div
        onClick={() =>
          openUpsellModal('order_skip', orderSkipEnabled, 'Order Skipped')
        }>
        <ToggleButton
          margin="0 4px 0 0"
          size={ToggleButtonSizeType.SMALL}
          checked={toggleChecked}
          disabled={toggleDisabled || !orderSkipEnabled}
          handleToggleClick={toggleAction}
          title={`order-toggle-${order.id}`}
        />
      </div>
      {!orderDelayEnabled ? (
        <Tooltip
          size="250px"
          side="bottom"
          content={<UpsellTooltip type="order_delay" />}>
          <span>{styledLabel}</span>
        </Tooltip>
      ) : (
        styledLabel
      )}
      {tooltip && <QuestionTooltip content={tooltip} margin="0 0 0 6px" />}
      {canEdit && orderDelayEnabled && (
        <Button
          btnType={ButtonType.TRANSPARENT_PRIMARY_NO_BORDER}
          width="34px"
          margin={0}
          onClick={editOrderAction}>
          {t('generic:Edit', 'Edit')}
        </Button>
      )}
    </>
  )
}

OrderStatusCellComponent.propTypes = {
  rowData: PropTypes.objectOf(PropTypes.any).isRequired,
  updateOrder: PropTypes.func.isRequired
}

const OrderStatusCell = memo(OrderStatusCellComponent)
export { OrderStatusCell }
